// exclusionBaseImage — 「人を外して戻す」のプレビューの土台 (世代 G) の画素を取る
// (exclusion-ui-investigation-2026-10-10.md「③-b」)。
//
//   1. 世代の署名 URL (`fetchRecoveryGenerationSnapshotUrls`) を取る
//   2. meta.json (背景色) と全レイヤの PNG を取る
//   3. `decodeExclusionBaseLayers` で decode (入室時の snapshot 読み込みと同じ経路)
//
// presigned URL の期限切れ (R2 は 403) は URL を取り直して**1 回だけ**再試行する。それ以外の
// 失敗 (404 / 5xx / ネットワーク / decode 失敗 / 寸法違い) は null = 呼び出し側は根から全 replay
// する fallback に倒す。

import { snapshotMetaSchema } from '@oekaki/protocol';
import type { BackgroundColor } from '../timelapse/backgroundAt.js';
import {
  type DecodeBaseLayersDeps,
  decodeExclusionBaseLayers,
  type ExclusionPreviewBase,
} from '../timelapse/exclusionPreviewFromBase.js';
import type { FrameSize } from '../timelapse/frameScale.js';
import type { FetchFn, PastCanvasClientDeps, PastCanvasSnapshotUrls } from './pastCanvasClient.js';
import {
  type FetchRecoveryGenerationSnapshotUrlsInput,
  type FetchRecoveryGenerationSnapshotUrlsOutcome,
  fetchRecoveryGenerationSnapshotUrls,
} from './recoveryGenerationsClient.js';

export interface LoadExclusionBaseImageInput {
  readonly dispatcherUrl: string;
  readonly roomId: string;
  readonly sessionId: string;
  readonly seq: number;
  readonly adminToken?: string;
  readonly canvas: FrameSize;
  readonly signal?: AbortSignal;
}

export interface LoadExclusionBaseImageDeps {
  readonly fetchFn?: FetchFn;
  readonly fetchUrls?: (
    input: FetchRecoveryGenerationSnapshotUrlsInput,
    deps?: PastCanvasClientDeps,
  ) => Promise<FetchRecoveryGenerationSnapshotUrlsOutcome>;
  readonly decode?: DecodeBaseLayersDeps;
}

type AttemptResult =
  | { readonly kind: 'ok'; readonly base: ExclusionPreviewBase }
  /** presigned URL の期限切れ (401/403)。URL を取り直せば通る見込み。 */
  | { readonly kind: 'expired' }
  | { readonly kind: 'failed' };

const isExpired = (status: number): boolean => status === 401 || status === 403;

const defaultFetch: FetchFn = (url, init) =>
  globalThis.fetch(url, init as RequestInit | undefined) as Promise<Response>;

/** 取れた G の土台。取れなければ null。 */
export const loadExclusionBaseImage = async (
  input: LoadExclusionBaseImageInput,
  deps: LoadExclusionBaseImageDeps = {},
): Promise<ExclusionPreviewBase | null> => {
  const fetchFn = deps.fetchFn ?? defaultFetch;
  const fetchUrls = deps.fetchUrls ?? fetchRecoveryGenerationSnapshotUrls;
  const initOf = (): { signal?: AbortSignal } =>
    input.signal !== undefined ? { signal: input.signal } : {};

  const getUrls = async (): Promise<PastCanvasSnapshotUrls | null> => {
    const res = await fetchUrls(
      {
        dispatcherUrl: input.dispatcherUrl,
        roomId: input.roomId,
        seq: input.seq,
        sessionId: input.sessionId,
        ...(input.adminToken !== undefined ? { adminToken: input.adminToken } : {}),
        ...(input.signal !== undefined ? { signal: input.signal } : {}),
      },
      { fetchFn },
    );
    return res.outcome === 'ok' ? res.urls : null;
  };

  const attempt = async (urls: PastCanvasSnapshotUrls): Promise<AttemptResult> => {
    try {
      const [metaRes, ...layerRes] = await Promise.all([
        fetchFn(urls.metaUrl, initOf()),
        ...urls.layerUrls.map((l) => fetchFn(l.url, initOf())),
      ]);
      if (metaRes === undefined) return { kind: 'failed' };
      if (isExpired(metaRes.status) || layerRes.some((r) => isExpired(r.status))) {
        return { kind: 'expired' };
      }
      if (!metaRes.ok || layerRes.some((r) => !r.ok)) return { kind: 'failed' };
      const meta = snapshotMetaSchema.safeParse(await metaRes.json());
      if (!meta.success) return { kind: 'failed' };
      const blobs: Array<{ id: number; blob: Blob }> = [];
      for (let i = 0; i < layerRes.length; i++) {
        const res = layerRes[i];
        const layer = urls.layerUrls[i];
        if (res === undefined || layer === undefined) return { kind: 'failed' };
        blobs.push({ id: layer.id, blob: await res.blob() });
      }
      const layers = await decodeExclusionBaseLayers(blobs, input.canvas, deps.decode);
      if (layers === null) return { kind: 'failed' };
      const background: BackgroundColor = {
        rgb: meta.data.background.rgb,
        alpha: meta.data.background.alpha,
      };
      return { kind: 'ok', base: { layers, background } };
    } catch {
      return { kind: 'failed' };
    }
  };

  const first = await getUrls();
  if (first === null) return null;
  const r1 = await attempt(first);
  if (r1.kind === 'ok') return r1.base;
  if (r1.kind === 'failed' || input.signal?.aborted === true) return null;
  // 期限切れ: URL を取り直して 1 回だけ再試行する。
  const second = await getUrls();
  if (second === null) return null;
  const r2 = await attempt(second);
  return r2.kind === 'ok' ? r2.base : null;
};
